import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT, useI18n } from "../i18n/I18nContext";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RefreshCw, Trash2, ShieldCheck, MailCheck, MailX, Mail, Send } from "lucide-react";
import { toast } from "sonner";

function useFmtDate() {
  const { lang } = useI18n();
  const locale = { it: "it-IT", en: "en-GB", de: "de-DE", es: "es-ES", fr: "fr-FR", ja: "ja-JP" }[lang] || "it-IT";
  return (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return iso;
    }
  };
}

export default function AdminUsers() {
  const { user } = useAuth();
  const t = useT();
  const fmtDate = useFmtDate();
  const [data, setData] = useState({ total: 0, users: [] });
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [composeTarget, setComposeTarget] = useState(null);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [sending, setSending] = useState(false);

  const openCompose = (u) => {
    setComposeTarget(u);
    setComposeSubject(t("admin.mail_subject"));
    setComposeBody(t("admin.mail_body", { name: u.first_name || "" }));
  };

  const closeCompose = () => {
    if (sending) return;
    setComposeTarget(null);
    setComposeSubject("");
    setComposeBody("");
  };

  const handleSend = async () => {
    if (!composeTarget || sending) return;
    setSending(true);
    try {
      await api.post("/admin/send-email", {
        to: composeTarget.email,
        subject: composeSubject,
        body: composeBody,
      });
      toast.success(t("admin.compose_sent", { email: composeTarget.email }));
      setComposeTarget(null);
      setComposeSubject("");
      setComposeBody("");
    } catch (e) {
      const err = e.response?.data?.detail || e.message;
      toast.error(t("admin.compose_error", { err }));
    } finally {
      setSending(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setData(data);
    } catch {
      setData({ total: 0, users: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user === null) return null; // still checking
  if (!user || user.role !== "admin") {
    return <Navigate to="/app" replace />;
  }

  const handleCleanup = async () => {
    if (!window.confirm(t("admin.cleanup_confirm"))) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup-test");
      const r = data.removed || {};
      toast.success(
        t("admin.cleanup_done", {
          users: r.users || 0,
          requests: r.requests || 0,
          messages: r.messages || 0,
        })
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 anim-fade-up" data-testid="admin-users-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">
            {t("admin.overline")}
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: "Outfit" }}>
            {t("admin.title")}
          </h1>
          <p className="text-mabw-soft mt-2 max-w-2xl">{t("admin.sub")}</p>
          <div className="text-sm text-mabw-soft mt-3" data-testid="admin-users-total">
            {t("admin.total", { n: data.total })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            className="btn-mabw-secondary"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {t("admin.refresh")}
          </button>
          <button
            onClick={handleCleanup}
            data-testid="admin-cleanup-btn"
            className="btn-mabw-ghost text-mabw-primary hover:bg-mabw-primary/10"
            disabled={cleaning}
          >
            <Trash2 className="w-4 h-4" />
            {t("admin.cleanup_btn")}
          </button>
        </div>
      </div>

      <div className="mt-8 card-mabw overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-mabw-soft" data-testid="admin-users-loading">
            {t("admin.loading")}
          </div>
        ) : data.users.length === 0 ? (
          <div className="p-10 text-center text-mabw-soft" data-testid="admin-users-empty">
            {t("admin.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-mabw-bg/60 text-xs uppercase tracking-[0.18em] text-mabw-soft">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">{t("admin.col_user")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("admin.col_email")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("admin.col_city")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("admin.col_joined")}</th>
                  <th className="text-center px-4 py-3 font-semibold">{t("admin.col_helps")}</th>
                  <th className="text-center px-4 py-3 font-semibold">{t("admin.col_open")}</th>
                  <th className="text-center px-4 py-3 font-semibold">{t("admin.col_verified")}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t("admin.col_role")}</th>
                  <th className="text-right px-4 py-3 font-semibold">{t("admin.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => {
                  const initials = `${(u.first_name || "")[0] || ""}${(u.last_name || "")[0] || ""}`.toUpperCase() || "?";
                  return (
                    <tr key={u.id} className="border-t border-mabw hover:bg-mabw-bg/40" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9 bg-mabw-secondary text-white shrink-0">
                            <AvatarFallback className="bg-mabw-secondary text-white text-xs">{initials}</AvatarFallback>
                          </Avatar>
                          <div className="font-medium text-mabw-text">
                            {u.first_name} {u.last_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-mabw-soft">{u.email}</td>
                      <td className="px-4 py-3 text-mabw-soft">{u.city || "—"}</td>
                      <td className="px-4 py-3 text-mabw-soft whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-center font-semibold text-mabw-secondary">{u.completed_helps}</td>
                      <td className="px-4 py-3 text-center text-mabw-soft">{u.open_requests}</td>
                      <td className="px-4 py-3 text-center">
                        {u.email_verified ? (
                          <span className="inline-flex items-center gap-1 text-mabw-secondary" title={t("admin.verified_yes")}>
                            <MailCheck className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-mabw-soft/60" title={t("admin.verified_no")}>
                            <MailX className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.role === "admin" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-mabw-primary/10 text-mabw-primary text-xs font-semibold">
                            <ShieldCheck className="w-3.5 h-3.5" /> {t("admin.role_admin")}
                          </span>
                        ) : (
                          <span className="text-xs text-mabw-soft">{t("admin.role_user")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openCompose(u)}
                          data-testid={`admin-mail-${u.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-mabw-secondary hover:bg-mabw-secondary/10 transition-colors"
                          title={t("admin.send_email")}
                        >
                          <Mail className="w-3.5 h-3.5" /> {t("admin.send_email")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!composeTarget} onOpenChange={(o) => { if (!o) closeCompose(); }}>
        <DialogContent data-testid="admin-compose-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              {t("admin.compose_title", { name: composeTarget ? `${composeTarget.first_name || ""} ${composeTarget.last_name || ""}`.trim() : "" })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-mabw-soft font-semibold mb-1">
                {t("admin.compose_from")}
              </label>
              <div className="px-3 py-2 rounded-lg bg-mabw-bg/60 text-sm text-mabw-text border border-mabw">
                {t("admin.compose_from_value")}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-mabw-soft font-semibold mb-1">
                {t("admin.compose_to")}
              </label>
              <div className="px-3 py-2 rounded-lg bg-mabw-bg/60 text-sm text-mabw-text border border-mabw" data-testid="compose-to-display">
                {composeTarget?.email}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-mabw-soft font-semibold mb-1" htmlFor="compose-subject">
                {t("admin.compose_subject")}
              </label>
              <input
                id="compose-subject"
                type="text"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                disabled={sending}
                data-testid="compose-subject-input"
                className="w-full px-3 py-2 rounded-lg border border-mabw bg-white text-sm focus:outline-none focus:ring-2 focus:ring-mabw-secondary/40"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-mabw-soft font-semibold mb-1" htmlFor="compose-body">
                {t("admin.compose_body")}
              </label>
              <textarea
                id="compose-body"
                rows={9}
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                disabled={sending}
                data-testid="compose-body-input"
                className="w-full px-3 py-2 rounded-lg border border-mabw bg-white text-sm focus:outline-none focus:ring-2 focus:ring-mabw-secondary/40 font-sans leading-relaxed"
              />
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <button
              type="button"
              onClick={closeCompose}
              disabled={sending}
              data-testid="compose-cancel-btn"
              className="btn-mabw-ghost"
            >
              {t("admin.compose_cancel")}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !composeSubject.trim() || !composeBody.trim()}
              data-testid="compose-send-btn"
              className="btn-mabw-primary"
            >
              <Send className="w-4 h-4" />
              {sending ? t("admin.compose_sending") : t("admin.compose_send")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
